import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { Table } from "@tanstack/react-table";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { useIsMobile } from "@/hooks/use-mobile";
import { useState, useEffect } from "react";

interface PaginationProps<TData> {
  table: Table<TData>;
}

export function Pagination<TData>({ table }: PaginationProps<TData>) {
  const {
    getState,
    setPageIndex,
    setPageSize,
    getPageCount,
    getCanPreviousPage,
    getCanNextPage,
    previousPage,
    nextPage,
  } = table;

  const isMobile = useIsMobile();
  const [isTablet, setIsTablet] = useState<boolean>(false);
  const [mobilePageSize, setMobilePageSize] = useState<number>(5);

  useEffect(() => {
    const checkTablet = () => {
      setIsTablet(window.innerWidth >= 768 && window.innerWidth < 1024);
    };

    const calculateMobilePageSize = () => {
      if (isMobile === true) {
        const screenHeight = window.innerHeight;
        // Estimate available space for table rows
        // Account for header (~64px), padding, pagination (~80px), and other UI elements (~200px)
        const availableHeight = screenHeight - 344;
        const estimatedRowHeight = 50;
        const calculatedRows = Math.floor(availableHeight / estimatedRowHeight);
        // Ensure minimum of 3 rows and maximum of 10 rows for mobile
        const optimalRows = Math.max(3, Math.min(calculatedRows, 10));
        setMobilePageSize(optimalRows);
      }
    };

    checkTablet();
    calculateMobilePageSize();

    window.addEventListener("resize", checkTablet);
    window.addEventListener("resize", calculateMobilePageSize);

    return () => {
      window.removeEventListener("resize", checkTablet);
      window.removeEventListener("resize", calculateMobilePageSize);
    };
  }, [isMobile]);

  const { pagination } = getState();
  const currentPage = pagination.pageIndex + 1;
  const totalPages = getPageCount();
  const rowsPerPage = pagination.pageSize;
  const totalRows = table.getFilteredRowModel().rows.length;

  const rowsPerPageOptions = [5, 10, 20, 50];

  useEffect(() => {
    if (isMobile === true && rowsPerPage !== mobilePageSize) {
      setPageSize(mobilePageSize);
    }
  }, [isMobile, mobilePageSize, rowsPerPage, setPageSize]);

  // Don't render until mobile detection is complete
  if (isMobile === undefined) {
    return (
      <div className="flex items-center justify-between px-2 mt-3">
        <div className="h-6 w-32 bg-muted animate-pulse rounded"></div>
        <div className="flex items-center gap-2">
          <div className="h-8 w-24 bg-muted animate-pulse rounded"></div>
          <div className="h-8 w-32 bg-muted animate-pulse rounded"></div>
        </div>
      </div>
    );
  }

  if (isMobile === true || isTablet) {
    return (
      <div className="flex flex-col gap-3 px-2 mt-3 md:flex-row md:items-center md:justify-between">
        {isTablet && (
          <div className="text-sm text-muted-foreground">
            {`${(currentPage - 1) * rowsPerPage + 1}-${Math.min(
              currentPage * rowsPerPage,
              totalRows
            )} of ${totalRows} rows`}
          </div>
        )}

        <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
          {isTablet && (
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Rows per page</span>
              <Select value={String(rowsPerPage)} onValueChange={(value) => setPageSize(Number(value))}>
                <SelectTrigger className="h-8 w-[70px] text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {rowsPerPageOptions.map((option) => (
                    <SelectItem key={option} value={String(option)}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Navigation controls - consistent for both mobile and tablet */}
          <div className="flex items-center justify-center gap-2">
            {/* First page button */}
            <button
              type="button"
              className={`border border-gray-300 rounded disabled:opacity-50 cursor-pointer hover:bg-gray-50 ${
                isMobile === true ? "p-2 touch-manipulation" : "p-1.5"
              }`}
              onClick={(e) => {
                e.preventDefault();
                setPageIndex(0);
              }}
              disabled={!getCanPreviousPage()}
            >
              <span className="sr-only">Go to first page</span>
              <ChevronsLeft size={isMobile === true ? 18 : 16} />
            </button>

            {/* Previous page button */}
            <button
              type="button"
              className={`border border-gray-300 rounded disabled:opacity-50 cursor-pointer hover:bg-gray-50 ${
                isMobile === true ? "p-2 touch-manipulation" : "p-1.5"
              }`}
              onClick={(e) => {
                e.preventDefault();
                previousPage();
              }}
              disabled={!getCanPreviousPage()}
            >
              <span className="sr-only">Go to previous page</span>
              <ChevronLeft size={isMobile === true ? 18 : 16} />
            </button>

            {/* Current page indicator */}
            <div className="flex items-center gap-2 text-sm font-medium min-w-[80px] justify-center">
              <span>{currentPage}</span>
              <span className="text-muted-foreground">of</span>
              <span>{totalPages}</span>
            </div>

            {/* Next page button */}
            <button
              type="button"
              className={`border border-gray-300 rounded disabled:opacity-50 cursor-pointer hover:bg-gray-50 ${
                isMobile === true ? "p-2 touch-manipulation" : "p-1.5"
              }`}
              onClick={(e) => {
                e.preventDefault();
                nextPage();
              }}
              disabled={!getCanNextPage()}
            >
              <span className="sr-only">Go to next page</span>
              <ChevronRight size={isMobile === true ? 18 : 16} />
            </button>

            {/* Last page button */}
            <button
              type="button"
              className={`border border-gray-300 rounded disabled:opacity-50 cursor-pointer hover:bg-gray-50 ${
                isMobile === true ? "p-2 touch-manipulation" : "p-1.5"
              }`}
              onClick={(e) => {
                e.preventDefault();
                setPageIndex(totalPages - 1);
              }}
              disabled={!getCanNextPage()}
            >
              <span className="sr-only">Go to last page</span>
              <ChevronsRight size={isMobile === true ? 18 : 16} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Desktop layout (original)
  return (
    <div className="flex items-center justify-between px-2 mt-3">
      <div className="flex-1 text-sm text-muted-foreground">
        {`${(currentPage - 1) * rowsPerPage + 1}-${Math.min(
          currentPage * rowsPerPage,
          totalRows
        )} of ${totalRows} rows`}
      </div>
      <div className="flex items-center space-x-6 lg:space-x-8">
        <div className="flex items-center space-x-2">
          <p className="text-sm font-medium">Rows per page</p>
          <Select value={String(rowsPerPage)} onValueChange={(value) => setPageSize(Number(value))}>
            <SelectTrigger className="h-8 w-[70px] text-sm font-medium border border-gray-300 rounded px-2 py-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {rowsPerPageOptions.map((option) => (
                <SelectItem key={option} value={String(option)}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex w-[100px] items-center justify-center text-sm font-medium">
          Page {currentPage} of {totalPages}
        </div>
        <div className="flex items-center space-x-2">
          <button
            type="button"
            className="hidden p-0.5 lg:flex border border-gray-300 rounded disabled:opacity-50 cursor-pointer"
            onClick={(e) => {
              e.preventDefault();
              setPageIndex(0);
            }}
            disabled={!getCanPreviousPage()}
          >
            <span className="sr-only">Go to first page</span>
            <ChevronsLeft />
          </button>
          <button
            type="button"
            className="p-0.5 border border-gray-300 rounded disabled:opacity-50 cursor-pointer"
            onClick={(e) => {
              e.preventDefault();
              previousPage();
            }}
            disabled={!getCanPreviousPage()}
          >
            <span className="sr-only">Go to previous page</span>
            <ChevronLeft />
          </button>
          <button
            type="button"
            className="p-0.5 border border-gray-300 rounded disabled:opacity-50 cursor-pointer"
            onClick={(e) => {
              e.preventDefault();
              nextPage();
            }}
            disabled={!getCanNextPage()}
          >
            <span className="sr-only">Go to next page</span>
            <ChevronRight />
          </button>
          <button
            type="button"
            className="hidden p-0.5 lg:flex border border-gray-300 rounded disabled:opacity-50 cursor-pointer"
            onClick={(e) => {
              e.preventDefault();
              setPageIndex(totalPages - 1);
            }}
            disabled={!getCanNextPage()}
          >
            <span className="sr-only">Go to last page</span>
            <ChevronsRight />
          </button>
        </div>
      </div>
    </div>
  );
}
